import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { Card, Button, Spinner } from '../components/ui';
import { buildSkyscannerUrl } from '@flymind/shared';

type PageState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'ready'; bookingUrl: string; origin: string; destination: string; departureDate: string; returnDate: string }
  | { kind: 'expired'; message: string };

export default function BookingRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useState<PageState>({ kind: 'loading' });

  const TP_MARKER = '516789';

  // Build affiliate booking URLs from route + dates
  function buildBookingUrls(origin: string, destination: string, depDate: string, retDate: string) {
    // Format dates for Aviasales: DDMM
    const depDDMM = depDate ? depDate.slice(8, 10) + depDate.slice(5, 7) : '';
    const retDDMM = retDate ? retDate.slice(8, 10) + retDate.slice(5, 7) : '';
    const aviasalesSearch = retDate
      ? `${origin}${depDDMM}${destination}${retDDMM}1`
      : `${origin}${depDDMM}${destination}1`;

    // Travelpayouts affiliate link (tracked with marker)
    const aviasales = `https://www.aviasales.com/search/${aviasalesSearch}?marker=${TP_MARKER}`;

    // Google Flights (no affiliate, but useful for price comparison)
    const google = retDate
      ? `https://www.google.com/travel/flights?q=Flights+from+${origin}+to+${destination}+on+${depDate}+return+${retDate}`
      : `https://www.google.com/travel/flights?q=Flights+from+${origin}+to+${destination}+on+${depDate}+one+way`;

    // Official Skyscanner referrals deep-link (shared helper) — lands on a real
    // results page instead of the homepage. The tp.media-wrapped slug bounced.
    const skyscanner = buildSkyscannerUrl({
      origin,
      destination,
      departureDate: depDate,
      returnDate: retDate || undefined,
    });

    return { aviasales, google, skyscanner };
  }

  useEffect(() => {
    const bookingUrl = searchParams.get('bookingUrl');
    const origin = searchParams.get('origin') || '';
    const destination = searchParams.get('destination') || '';
    const departureDate = searchParams.get('departureDate') || '';
    const returnDate = searchParams.get('returnDate') || '';

    if (!bookingUrl && !origin) {
      setState({ kind: 'invalid' });
      return;
    }

    setState({
      kind: 'ready',
      bookingUrl: bookingUrl || '',
      origin,
      destination,
      departureDate,
      returnDate,
    });
  }, [searchParams]);

  // No auto-redirect — user chooses the booking partner

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">
      {/* Branding */}
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-bold text-primary-600">FlyMind</h1>
        <p className="text-sm text-text-secondary mt-1">
          {t('bookingRedirect.tagline')}
        </p>
      </div>

      {/* Loading */}
      {state.kind === 'loading' && (
        <Card variant="elevated" padding="lg" className="w-full max-w-md text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-text-primary font-medium">
            {t('bookingRedirect.checking')}
          </p>
        </Card>
      )}

      {/* Invalid link */}
      {state.kind === 'invalid' && (
        <Card variant="elevated" padding="lg" className="w-full max-w-md text-center">
          <div className="mb-4 w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            {t('bookingRedirect.invalidTitle')}
          </h2>
          <p className="text-text-secondary text-sm mb-6">
            {t('bookingRedirect.invalidDescription')}
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            {t('bookingRedirect.backToDashboard')}
          </Button>
        </Card>
      )}

      {/* Ready — show booking partner links */}
      {state.kind === 'ready' && (
        <Card variant="elevated" padding="lg" className="w-full max-w-md">
          {/* Route header */}
          {state.origin && state.destination && (
            <div className="flex items-center gap-3 mb-4">
              <div>
                <p className="font-semibold text-text-primary">
                  {state.origin} &rarr; {state.destination}
                </p>
                {state.departureDate && (
                  <p className="text-sm text-text-secondary">
                    {state.departureDate}{state.returnDate ? ` - ${state.returnDate}` : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Booking partner links */}
          <div>
            <p className="text-text-secondary text-sm mb-3 text-center">
              {t('bookingRedirect.chooseWhere')}
            </p>
            {(() => {
              const urls = state.origin && state.destination
                ? buildBookingUrls(state.origin, state.destination, state.departureDate, state.returnDate)
                : { aviasales: state.bookingUrl || '#', google: '#', skyscanner: '#' };
              // If we have a direct bookingUrl from the search, use it as the primary link
              const primaryUrl = state.bookingUrl || urls.aviasales;
              return (
                <div className="space-y-2">
                  <a
                    href={primaryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full px-4 py-3 rounded-lg border-2 border-primary-200 bg-primary-50 hover:bg-primary-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg" aria-hidden="true">&#9992;&#65039;</span>
                      <div>
                        <span className="font-semibold text-text-primary">{t('bookingRedirect.bestPriceSearch')}</span>
                        <span className="block text-xs text-text-secondary">{t('bookingRedirect.poweredByAviasales')}</span>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href={urls.skyscanner}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-background hover:bg-surface-secondary transition-colors"
                  >
                    <span className="font-medium text-text-primary">Skyscanner</span>
                    <svg className="w-4 h-4 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <a
                    href={urls.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-background hover:bg-surface-secondary transition-colors"
                  >
                    <span className="font-medium text-text-primary">Google Flights</span>
                    <svg className="w-4 h-4 text-text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              );
            })()}
            <p className="text-xs text-text-secondary text-center mt-3">
              {t('bookingRedirect.pricesMayVary')}
            </p>
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-primary-600 underline hover:text-primary-700 cursor-pointer"
              >
                {t('bookingRedirect.backToDashboard')}
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Expired / error */}
      {state.kind === 'expired' && (
        <Card variant="elevated" padding="lg" className="w-full max-w-md text-center">
          <div className="mb-4 w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
            <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            {state.message}
          </h2>
          <p className="text-text-secondary text-sm mb-6">
            {t('bookingRedirect.pricesChangeQuickly')}
          </p>
          <Button onClick={() => navigate('/dashboard')}>
            {t('bookingRedirect.backToDashboard')}
          </Button>
        </Card>
      )}
    </div>
  );
}
